import Axios from 'axios';
import { toastr } from 'react-redux-toastr';
import { createAction } from 'redux-act';
import { editorUrl, url } from 'utils/url';
import { errorMsg, log } from 'utils';
import fileDownload from 'js-file-download';
import moment from 'moment';


export const POIS_FETCH_DATA_INIT = createAction('POIS_FETCH_DATA_INIT');
export const POIS_FETCH_DATA_SUCCESS = createAction('POIS_FETCH_DATA_SUCCESS');
export const POIS_FETCH_DATA_FAIL = createAction('POIS_FETCH_DATA_FAIL');

export const POIS_FETCH_SHAPE_INIT = createAction('POIS_FETCH_SHAPE_INIT');
export const POIS_FETCH_SHAPE_SUCCESS = createAction(
  'POIS_FETCH_SHAPE_SUCCESS'
);
export const POIS_FETCH_SHAPE_FAIL = createAction('POIS_FETCH_SHAPE_FAIL');

export const POIS_DELETE_POI_INIT = createAction('POIS_DELETE_POI_INIT');
export const POIS_DELETE_POI_SUCCESS = createAction('POIS_DELETE_POI_SUCCESS');
export const POIS_DELETE_POI_FAIL = createAction('POIS_DELETE_POI_FAIL');

export const POIS_CLEAR_DATA = createAction('POIS_CLEAR_DATA');

export const POIS_CREATE_POI_INIT = createAction('POIS_CREATE_POI_INIT');
export const POIS_CREATE_POI_SUCCESS = createAction('POIS_CREATE_POI_SUCCESS');
export const POIS_CREATE_POI_FAIL = createAction('POIS_CREATE_POI_FAIL');

export const POIS_MODIFY_POI_INIT = createAction('POIS_MODIFY_POI_INIT');
export const POIS_MODIFY_POI_SUCCESS = createAction('POIS_MODIFY_POI_SUCCESS');
export const POIS_MODIFY_POI_FAIL = createAction('POIS_MODIFY_POI_FAIL');

export const POIS_CLEAN_UP = createAction('POIS_CLEAN_UP');
export const POIS_CLEAN_UP_FORM = createAction('POIS_CLEAN_UP_FORM');

export const POIS_CLEAR_DATA_LOGOUT = createAction('POIS_CLEAR_DATA_LOGOUT');
export const POIS_EXPORT_POI_INIT = createAction('POIS_EXPORT_POI_INIT');
export const POIS_EXPORT_POI_SUCCESS = createAction('POIS_EXPORT_POI_SUCCESS');
export const POIS_EXPORT_POI_FAIL = createAction('POIS_EXPORT_POI_FAIL');


export const fetchPois = ({
  poiId,
  siteId,
  categoryId = 'all',
  type = 'published',
}) => {
  return async (dispatch, getState) => {
    dispatch(POIS_FETCH_DATA_INIT());

    // --- Fetch Single POI (Existing Logic) ---
    if (poiId) {
      let poiData;
      try {
        poiData = await Axios.get(`${url}/poi/list`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
          params: {
            siteId,
          },
        });
      } catch (error) {
        toastr.error('', `${errorMsg(dispatch, error)}`);
        return dispatch(POIS_FETCH_DATA_FAIL({ error }));
      }
      const poiFind = poiData.data.data.find((x) => x._id === poiId);
      const poi = { ...poiFind, id: poiId };
      return dispatch(
        POIS_FETCH_DATA_SUCCESS({
          list: getState().pois.list,
          poi,
        })
      );
    }

    // --- Fetch Multiple POIs ---
    if (siteId) {
      let poisResponse;
      try {
        poisResponse = await Axios.post(
          `${url}/poi/filter`,
          {
            siteId,
            categoryId,
            type,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          }
        );
      } catch (error) {
        let errorMessage;
        if (error.response) {
          errorMessage = error && error.response && error.response.data.message;
        }
        toastr.error('', errorMessage);
        return dispatch(POIS_FETCH_DATA_FAIL({ error }));
      }

      // Filter by siteId (assuming backend might return other siteIds, though usually not for 'filter' endpoint)
      let pois = poisResponse.data.data.filter((poi) => poi.siteId === siteId);

      const { shapes: shapeLookupMap } = getState().editors; // Destructure and rename for clarity
      const site = getState().sites.list.find(
        (siteInfo) => siteInfo._id === siteId
      );

      console.log(shapeLookupMap, 'shape look up');
      // Early exit if shapes map is invalid or empty
      if (!(shapeLookupMap instanceof Map) || shapeLookupMap.size === 0) {
        console.warn(
          "Shape lookup map is empty or invalid. 'connectionStatus' will be 'none' for all POIs."
        );

        pois = pois.map((poi) => ({
          ...poi,
          location: site.isWayfinding ? [] : poi.location || '', // Ensure location is an array if wayfinding, else original

          hasShape: 'none', // Ensure hasShape is boolean
        }));
      } else {
        // --- Optimized POI mapping ---
        pois = pois.map((poi) => {
          let locationsToCheck = []; // Array of string IDs to verify against shapes map
          let originalLocationField = poi.location; // Store original location for fallback

          // Determine the source of locations and populate locationsToCheck
          if (site.isWayfinding) {
            // If wayfinding, prioritize 'locations' array and map original 'location' field if needed
            if (Array.isArray(poi.locations) && poi.locations.length > 0) {
              locationsToCheck = poi.locations.map((loc) =>
                String(loc.id).trim()
              ); // Ensure string and trim
            } else {
              locationsToCheck = []; // No valid array locations for wayfinding
            }
          } else {
            // Not wayfinding, use 'location' string field
            if (
              typeof poi.location === 'string' &&
              poi.location.trim() !== ''
            ) {
              locationsToCheck = poi.location
                .split(',')
                .map((loc) => loc.trim());
            } else {
              locationsToCheck = []; // No valid string locations for non-wayfinding
            }
          }

          let foundCount = 0;
          const totalLocations = locationsToCheck.length;
          let connectionStatus = 'none';
          let mappedLocationNames = []; // To store names if wayfinding, or original string if not

          if (totalLocations > 0) {
            for (const locIdOrName of locationsToCheck) {
              // Ensure consistent lookup based on how shape names/IDs are stored in the map
              // If shape IDs are stored as they are, and names are lowercased, then:
              const shape = shapeLookupMap.get(locIdOrName); // Try lookup by ID first (case-sensitive)
              // If not found by ID, try lookup by lowercased name
              const shapeByName = shapeLookupMap.get(locIdOrName);

              if (shape || shapeByName) {
                foundCount++;
                // If wayfinding and we are mapping to names, collect the actual names
                if (site.isWayfinding) {
                  const foundShape = shape || shapeByName; // Use the one that matched
                  if (foundShape && foundShape.name) {
                    mappedLocationNames.push(foundShape.name);
                  } else {
                    // Fallback if shape is found but has no name, or just use the ID
                    mappedLocationNames.push(locIdOrName);
                  }
                }
              }
            }

            if (foundCount === totalLocations) {
              connectionStatus = 'full';
            } else if (foundCount > 0) {
              connectionStatus = 'partial';
            }
          }

          // Determine the 'location' field value based on site.isWayfinding
          let finalLocationValue;
          if (site.isWayfinding) {
            // If wayfinding, 'location' field should be the array of mapped names
            finalLocationValue = mappedLocationNames.join(', ');
          } else {
            // If not wayfinding, 'location' field should retain its original string value
            finalLocationValue = originalLocationField;
          }

          return {
            ...poi,
            location: finalLocationValue, // Updated location field (array of names or original string)
            hasShape: connectionStatus, // Boolean for convenience
          };
        });
      }

      return dispatch(
        POIS_FETCH_DATA_SUCCESS({
          list: pois,
          poi: getState().pois.poi, // Ensure this still references the correct poi (if any)
        })
      );
    }
  };
};

export const fetchShape = ({ siteId }) => {
  // eslint-disable-next-line
  return async (dispatch, getState) => {
    dispatch(POIS_FETCH_SHAPE_INIT());

    if (siteId) {
      let shape;
      try {
        shape = await Axios.get(`${editorUrl}/site/open/${siteId}`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        let errorMessage;
        if (error.response) {
          errorMessage = error && error.response && error.response.data.message;
        }
        // toastr.error('', errorMessage);
        log(errorMessage);
        return dispatch(POIS_FETCH_SHAPE_FAIL({ error }));
      }
      shape = shape.data.data.freeShapes;

      if (Array.isArray(shape)) {
        const shapeNames = new Set(shape.map((sh) => sh.name));

        console.log(shapeNames);
        return dispatch(
          POIS_FETCH_SHAPE_SUCCESS({
            shape: shapeNames,
          })
        );
      }
      toastr.error('', 'Shape is empty');
      return dispatch(POIS_FETCH_SHAPE_FAIL({ error: 'Shape is empty' }));
    } else {
      return dispatch(
        POIS_FETCH_SHAPE_SUCCESS({
          shape: [],
        })
      );
    }
  };
};

export const removePoi = ({ id, siteId }, cb) => {
  return async (dispatch) => {
    dispatch(POIS_DELETE_POI_INIT());
    try {
      await Axios.post(
        `${url}/poi/remove`,
        {
          poiId: id,
          siteId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
    } catch (error) {
      let errorMessage;
      if (error.response) {
        errorMessage = error && error.response && error.response.data.message;
      }
      toastr.error('', errorMessage);
      return dispatch(
        POIS_DELETE_POI_FAIL({
          error: error && error.response && error.response.data,
        })
      );
    }

    toastr.success('', 'The poi was removed.');
    cb();
    return dispatch(POIS_DELETE_POI_SUCCESS({ id }));
  };
};

export const deletePoi = ({ id, siteId }, cb) => {
  return async (dispatch) => {
    dispatch(POIS_DELETE_POI_INIT());
    try {
      await Axios.post(
        `${url}/poi/delete`,
        {
          poiId: id,
          siteId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
    } catch (error) {
      let errorMessage;
      if (error.response) {
        errorMessage = error && error.response && error.response.data.message;
      }
      toastr.error('', errorMessage);
      return dispatch(
        POIS_DELETE_POI_FAIL({
          error: error && error.response && error.response.data,
        })
      );
    }

    toastr.success('', 'The poi was deleted.');
    cb();
    return dispatch(POIS_DELETE_POI_SUCCESS({ id }));
  };
};

export const clearPoisData = () => {
  return (dispatch) => {
    dispatch(POIS_CLEAR_DATA());
  };
};

export const clearPoisDataLogout = () => {
  return (dispatch) => {
    dispatch(POIS_CLEAR_DATA_LOGOUT());
  };
};

export const createPoi = ({
  name,
  phone,
  priority,
  description,
  file,
  opentime,
  url: poiUrl,
  categoryId,
  categoryName,
  floorId,
  floorName,
  floorIds,
  floorNames,
  siteId,
  location,
  locations,
  floor,
  tags,
  firstFileList,
  secondFileList,
  categoryIds,
  subCategoryIds,
  start,
  end,
}) => {
  return async (dispatch) => {
    dispatch(POIS_CREATE_POI_INIT());

    // let createPoi;

    const poi = new FormData();
    poi.set('path', 'poi');
    poi.set('name', name);
    poi.set('phone', phone);
    poi.set('priority', priority);
    poi.set('description', description);
    poi.set('opentime', opentime);
    poi.set('url', poiUrl);
    if (categoryId) {
      poi.set('categoryId', categoryId);
      poi.set('categoryName', categoryName);
    }
    if (tags) poi.set('tags', JSON.stringify(tags));
    poi.set('location', location);
    if (locations) {
      poi.set('locations', JSON.stringify(locations));
      poi.set('location', locations.join(', '));
    }
    if (categoryIds.length > 0) {
      poi.set('categoryIds', JSON.stringify(categoryIds));
      poi.set('categoryId', categoryIds[0]);
    }
    if (subCategoryIds)
      poi.set('subCategoryIds', JSON.stringify(subCategoryIds));

    if (floorId) {
      poi.set('floorId', floorId);
      poi.set('floorName', floorName);
    }
    if (floorIds && floorIds.length > 0) {
      poi.set('floorIds', JSON.stringify(floorIds));
      poi.set('floorId', floorIds[0]);
    }
    if (floorNames && floorNames.length > 0) {
      poi.set('floorNames', JSON.stringify(floorNames));
      poi.set('floorName', floorNames[0]);
    }
    poi.set('siteId', siteId);
    poi.set('floor', floor);
    if (file) poi.append('file', file, `logo-${file.name}`);
    if (firstFileList) {
      for (let ind = 0, l = firstFileList.length; ind < l; ind += 1) {
        if (firstFileList[ind])
          poi.append(
            'file',
            firstFileList[ind],
            `firstMedias-${firstFileList[ind].name}`
          );
      }
    }
    if (secondFileList) {
      for (let ind = 0, l = secondFileList.length; ind < l; ind += 1) {
        if (secondFileList[ind])
          poi.append(
            'file',
            secondFileList[ind],
            `secondMedias-${secondFileList[ind].name}`
          );
      }
    }
    if (start) {
      poi.set('start', moment(start).format('YYYY-MM-DD'));
    }

    if (end) poi.set('end', moment(end).format('YYYY-MM-DD'));
    try {
      await Axios.post(`${url}/poi/create`, poi, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'content-type': 'multipart/form-data',
        },
      });
    } catch (error) {
      // let errorMessage;
      // if (error.response) {
      //   errorMessage = error && error.response && error.response.data.message;
      // }

      toastr.error('', 'Please fill in all required fields');
      // toastr.error('', errorMessage);
      return dispatch(
        POIS_CREATE_POI_FAIL({
          error: error && error.response && error.response.data,
        })
      );
    }
    const poiJson = JSON.stringify(Object.fromEntries(poi));
    toastr.success('', 'Poi created successfully');
    return dispatch(POIS_CREATE_POI_SUCCESS({ poi: poiJson }));
  };
};

export const modifyPoi = ({
  id,
  name,
  phone,
  priority,
  description,
  file,
  logo,
  opentime,
  url: poiUrl,
  categoryId,
  categoryName,
  floorId,
  floorName,
  floorIds,
  floorNames,
  tags,
  siteId,
  location,
  locations,
  floor,
  firstMedias,
  secondMedias,
  firstFileList,
  secondFileList,
  categoryIds,
  subCategoryIds,
  start,
  end,
}) => {
  return async (dispatch) => {
    dispatch(POIS_MODIFY_POI_INIT());

    const poi = new FormData();
    poi.set('path', 'poi');
    poi.set('id', id);
    poi.set('name', name);
    poi.set('phone', phone);
    poi.set('priority', priority);
    poi.set('description', description);
    poi.set('opentime', opentime);
    poi.set('url', poiUrl);
    if (categoryId && categoryName) {
      poi.set('categoryId', categoryId);
      poi.set('categoryName', categoryName);
    }
    if (categoryIds.length > 0) {
      poi.set('categoryIds', JSON.stringify(categoryIds));
      poi.set('categoryId', categoryIds[0]);
    }
    if (subCategoryIds)
      poi.set('subCategoryIds', JSON.stringify(subCategoryIds));
    if (floorId && floorName) {
      poi.set('floorId', floorId);
      poi.set('floorName', floorName);
    }
    if (floorIds && floorIds.length > 0) {
      poi.set('floorIds', JSON.stringify(floorIds));
      poi.set('floorId', floorIds[0]);
    }
    if (floorNames && floorNames.length > 0) {
      poi.set('floorNames', JSON.stringify(floorNames));
      poi.set('floorName', floorNames[0]);
    }
    poi.set('siteId', siteId);
    if (tags) poi.set('tags', JSON.stringify(tags));
    if (locations) {
      poi.set('locations', JSON.stringify(locations));
      if (locations.length > 0) poi.set('location', locations.join(', '));
    }
    poi.set('location', location);
    poi.set('floor', floor);
    poi.set('firstMedias', JSON.stringify(firstMedias));
    poi.set('secondMedias', JSON.stringify(secondMedias));
    if (logo) {
      poi.set('logo', logo);
    }
    if (file) poi.append('file', file, `logo-${file.name}`);
    if (firstFileList) {
      for (let ind = 0, l = firstFileList.length; ind < l; ind += 1) {
        if (firstFileList[ind])
          poi.append(
            'file',
            firstFileList[ind],
            `firstMedias-${firstFileList[ind].name}`
          );
      }
    }
    if (secondFileList) {
      for (let ind = 0, l = secondFileList.length; ind < l; ind += 1) {
        if (secondFileList[ind])
          poi.append(
            'file',
            secondFileList[ind],
            `secondMedias-${secondFileList[ind].name}`
          );
      }
    }

    if (start) {
      console.log(start, end, 'start and end scheduling');
      poi.set('start', moment(start).format('YYYY-MM-DD'));
    }

    if (end) poi.set('end', moment(end).format('YYYY-MM-DD'));

    try {
      await Axios.put(`${url}/poi/edit`, poi, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'content-type': 'multipart/form-data',
        },
      });
    } catch (error) {
      let errorMessage;
      if (error.response) {
        errorMessage = error && error.response && error.response.data.message;
      }
      toastr.error('', errorMessage);
      return dispatch(
        POIS_MODIFY_POI_FAIL({
          error: error && error.response && error.response.data,
        })
      );
    }
    const poiJson = JSON.stringify(Object.fromEntries(poi));
    toastr.success('', 'Poi updated successfully');
    return dispatch(POIS_MODIFY_POI_SUCCESS({ poiJson, id }));
  };
};

export const downloadPois = ({ siteId, siteName }) => {
  return async (dispatch) => {
    dispatch(POIS_EXPORT_POI_INIT());
    Axios.get(`${url}/poi/export`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
      responseType: 'blob',
      params: {
        siteId,
      },
    })
      .then((res) => {
        fileDownload(
          res.data,
          `poi_export_${siteName}_${moment(new Date()).format(
            'YYYY-MM-DD'
          )}.xlsx`
        );
        return dispatch(POIS_EXPORT_POI_SUCCESS());
      })
      .catch((error) => {
        toastr.error('', `${errorMsg(dispatch, error)}`);
        return dispatch(POIS_EXPORT_POI_FAIL({ error }));
      });
  };
};

export const bulkRecovery = ({ status, ids, siteId }, cb) => {
  return async () => {
    try {
      await Axios.post(
        `${url}/poi/bulk-status-change `,
        {
          status,
          ids,
          siteId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
    } catch (e) {
      console.log(e, 'error');
      toastr.error('', 'Something went wrong');
    }
    cb();
  };
};

export const bulkApproval = ({ status, ids, siteId }, cb) => {
  return async () => {
    await Axios.post(
      `${url}/poi/approval`,
      {
        status,
        ids,
        siteId,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      }
    );
    cb();
  };
};

export const poisCleanUp = () => (dispatch) => dispatch(POIS_CLEAN_UP());
export const poisCleanUpForm = () => (dispatch) =>
  dispatch(POIS_CLEAN_UP_FORM());
